import { Button, message, Modal } from 'antd';
import {
    DashboardOutlined,
    HistoryOutlined,
    HomeOutlined,
    LineChartOutlined,
    LogoutOutlined,
    MoonOutlined,
    PlusOutlined,
    SnippetsOutlined,
    SunOutlined,
    UserOutlined,
} from '@ant-design/icons';
import { AppShell } from '@dowty/ui';
import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useOnlineStatus } from '@dowty/offline';
import { useLocation, useNavigate } from 'react-router-dom';
import { offlineDb, userOutbox } from '../offline';
import { isGerman } from '../utils/language';
import GRPC_BACKEND_URL, {dowtexHubUrl, SSO_ENABLED, SSO_ISSUER_URL} from '../grpcConfig';
import RouterLegalFooter from './RouterLegalFooter';

interface SidebarProps {
    onAddActivityClick: () => void;
    isDark: boolean;
    toggleTheme: () => void;
    username?: string | null;
    children: ReactNode;
}

const logout = async () => {
    const pending = await userOutbox.list();
    if (pending.length > 0 && !await new Promise<boolean>(resolve => Modal.confirm({
        title: isGerman() ? 'Nicht gespeicherte Aktivitäten' : 'Unsaved activities',
        content: isGerman() ? `${pending.length} Aktivitäten gehen beim Abmelden verloren. Trotzdem abmelden?` : `${pending.length} activities will be lost when you sign out. Continue?`,
        okText: isGerman() ? 'Abmelden' : 'Sign out',
        cancelText: isGerman() ? 'Abbrechen' : 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
    }))) return;
    if (SSO_ENABLED && sessionStorage.getItem('sportdings-sso-session') === 'true') {
        try {
            const response = await fetch(new URL('/auth/sso/logout', GRPC_BACKEND_URL).toString(), { method: 'POST', credentials: 'include' });
            if (!response.ok) throw new Error('SSO logout failed');
        } catch {
            message.error(isGerman() ? 'Abmeldung fehlgeschlagen. Bitte erneut versuchen.' : 'Sign-out failed. Please try again.');
            return;
        }
        sessionStorage.removeItem('sportdings-sso-session');
    }
    localStorage.clear();
    await offlineDb.wipe().catch(() => undefined);
    if (SSO_ENABLED) {
        try { sessionStorage.setItem('dowtex-sso-silent', 'signed-out'); } catch { /* Storage may be disabled. */ }
        const form = document.createElement('form');
        form.hidden = true;
        form.method = 'POST';
        form.action = new URL('/sso/signout', SSO_ISSUER_URL).toString();
        const clientId = document.createElement('input');
        clientId.type = 'hidden';
        clientId.name = 'client_id';
        clientId.value = 'sportdings';
        form.append(clientId);
        document.body.append(form);
        form.submit();
        return;
    }
    window.location.reload();
};

const Sidebar: React.FC<SidebarProps> = ({ onAddActivityClick, isDark, toggleTheme, username, children }) => {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const isOnline = useOnlineStatus();
    const moreLabel = isGerman() ? 'Weitere Navigation' : 'More navigation';
    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (!(event.target instanceof Element) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            const link = event.target.closest<HTMLAnchorElement>('a[href]');
            if (!link || (!link.closest('.ui-shell') && !link.closest(`.ant-drawer-body nav[aria-label="${moreLabel}"]`))) return;
            const href = link.getAttribute('href');
            if (href === '#ui-main') return;
            if (!href?.startsWith('/')) return;

            event.preventDefault();
            navigate(href);
        };
        document.addEventListener('click', handleClick);
        return () => document.removeEventListener('click', handleClick);
    }, [navigate, moreLabel]);

    return <AppShell
        items={[
            { href: '/dashboard', label: 'Dashboard', icon: <DashboardOutlined /> },
            { href: '/history', label: isGerman() ? 'Aktivitätsverlauf' : 'Activity history', icon: <HistoryOutlined /> },
            { href: '/user', label: 'Profile', icon: <UserOutlined /> },
            { href: '/templates', label: 'Templates', icon: <SnippetsOutlined /> },
            { href: '/statistics', label: isGerman() ? 'Statistiken' : 'Statistics', icon: <LineChartOutlined /> },
        ]}
        currentPath={pathname}
        moreLabel={moreLabel}
        labels={{ more: isGerman() ? 'Mehr' : 'More' }}
        brand={<div className="sportdings-brand"><img src="/logo.png" width={32} height={32} alt="" /><strong>SportDings</strong></div>}
        primaryAction={<Button type="primary" aria-label="Log activity" onClick={onAddActivityClick}><PlusOutlined />Log activity</Button>}
        account={{
            name: username ?? 'Account',
            actions: [
                { key: 'theme', label: isDark ? 'Light mode' : 'Dark mode', icon: isDark ? <SunOutlined /> : <MoonOutlined />, onSelect: toggleTheme },
                { key: 'hub', label: 'Dowtex hub', icon: <HomeOutlined />, href: dowtexHubUrl() },
                { key: 'logout', label: 'Log out', icon: <LogoutOutlined />, onSelect: () => void logout() },
            ],
        }}
        legal={<RouterLegalFooter />}
    >
        {!isOnline && <output className="sf-offline-status">{isGerman() ? 'Offline: Änderungen werden auf diesem Gerät gespeichert' : 'Offline: changes are saved on this device'}</output>}
        {children}
    </AppShell>;
};

export default Sidebar;
